/**
 * Wischgeste „Zeile nach links wegwischen“ (K2) als reine Zustandsmaschine ohne DOM:
 * `idle → pending → dragging ⇄ armed → committed | cancelled`.
 *
 * - Start in den äußeren 24 px: nie eine Geste (dort liegen die Zurück-Gesten von Android und iOS).
 * - Die Zeile wird erst übernommen, wenn die Bewegung mehr als 10 px waagerecht ist **und** |dx| > 1,5·|dy|.
 *   Überwiegt vorher die senkrechte Bewegung, ist es Scrollen (`cancelled`).
 * - Nur nach links. Ab der Schwelle (40 % der Zeilenbreite, mindestens 120 px, höchstens 220 px) ist die Geste
 *   „scharf“; Loslassen dort entfernt. Ein schneller Wurf (dx ≤ −64 px, über 0,5 px/ms) genügt auch darunter.
 */

export const SWIPE_EDGE_PX = 24;
export const SWIPE_LOCK_PX = 10;
export const SWIPE_RATIO = 1.5;
export const SWIPE_MIN_PX = 120;
export const SWIPE_MAX_PX = 220;
export const SWIPE_FLING_PX = 64;
export const SWIPE_FLING_SPEED = 0.5;

export type SwipePhase = 'idle' | 'pending' | 'dragging' | 'armed' | 'committed' | 'cancelled';

export interface SwipeState {
  phase: SwipePhase;
  startX: number;
  startY: number;
  startAt: number;
  /** Verschiebung der Zeile in px (nie positiv: nur nach links). */
  offset: number;
}

export const SWIPE_IDLE: SwipeState = {
  phase: 'idle',
  startX: 0,
  startY: 0,
  startAt: 0,
  offset: 0,
};

/** Strecke, ab der Loslassen entfernt. */
export function swipeThreshold(rowWidth: number): number {
  return Math.min(SWIPE_MAX_PX, Math.max(SWIPE_MIN_PX, 0.4 * rowWidth));
}

/** Aufsetzen des Fingers. Im Randstreifen (links und rechts) gibt es keine Geste. */
export function swipeStart(x: number, y: number, at: number, viewportWidth: number): SwipeState {
  const edge = x < SWIPE_EDGE_PX || x > viewportWidth - SWIPE_EDGE_PX;
  return { phase: edge ? 'cancelled' : 'pending', startX: x, startY: y, startAt: at, offset: 0 };
}

/** Bewegung des Fingers. */
export function swipeMove(state: SwipeState, x: number, y: number, rowWidth: number): SwipeState {
  if (state.phase === 'idle' || state.phase === 'cancelled' || state.phase === 'committed') {
    return state;
  }
  const dx = x - state.startX;
  const dy = y - state.startY;
  if (state.phase === 'pending') {
    if (Math.abs(dx) > SWIPE_LOCK_PX && Math.abs(dx) > SWIPE_RATIO * Math.abs(dy)) {
      // Übernommen: ab hier folgt die Zeile dem Finger (nur nach links)
      return swipeMove({ ...state, phase: 'dragging' }, x, y, rowWidth);
    }
    // Senkrecht überwiegt (oder nach rechts gewischt): Scrollen, keine Geste mehr bis zum Loslassen
    if (Math.abs(dy) > SWIPE_LOCK_PX || dx > SWIPE_LOCK_PX) return { ...state, phase: 'cancelled' };
    return state;
  }
  const offset = Math.min(0, dx);
  return {
    ...state,
    offset,
    phase: -offset >= swipeThreshold(rowWidth) ? 'armed' : 'dragging',
  };
}

/** Loslassen: `committed` (entfernen) oder `cancelled` (zurückfedern). */
export function swipeEnd(
  state: SwipeState,
  x: number,
  y: number,
  now: number,
  rowWidth: number,
): SwipeState {
  if (state.phase !== 'dragging' && state.phase !== 'armed') {
    return { ...state, phase: state.phase === 'committed' ? 'committed' : 'cancelled', offset: 0 };
  }
  const moved = swipeMove(state, x, y, rowWidth);
  const dx = x - state.startX;
  const dy = y - state.startY;
  const ms = now - state.startAt;
  const fling =
    dx <= -SWIPE_FLING_PX &&
    Math.abs(dx) > SWIPE_RATIO * Math.abs(dy) &&
    ms > 0 &&
    Math.abs(dx) / ms > SWIPE_FLING_SPEED;
  if (moved.phase === 'armed' || fling) return { ...moved, phase: 'committed' };
  return { ...moved, phase: 'cancelled', offset: 0 };
}
